/**
 * Navegación de escritorio.
 *
 * Tenía 17 entradas, una por colección de Mongo: Actividad, Tarjetas, Deudas,
 * Recurrentes, Cargos detectados, Calendario, Análisis, Metas, Conciliación,
 * Aprobaciones, Familia, Categorías, Comisiones… Elegir la correcta exigía
 * saber cómo está organizada la base de datos.
 *
 * Ahora son cuatro preguntas y un botón:
 *   Inicio       ¿cómo estoy hoy?
 *   Movimientos  ¿qué pasó con mi dinero?
 *   Por pagar    ¿qué tengo que pagar y cuándo?
 *   Mi dinero    ¿dónde está y cuánto tengo?
 *
 * Lo demás no desapareció: sigue accesible desde donde tiene sentido (las
 * herramientas de fin de mes en «Más», el perfil abajo). Dejó de competir por
 * la atención con lo que se usa todos los días.
 */
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Home, ArrowLeftRight, CalendarClock, Wallet, Plus, LogOut, MoreHorizontal,
} from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import MoreSheet from "@/components/MoreSheet";

export const MAIN_NAV = [
  { to: "/", label: "Inicio", icon: Home, tid: "nav-inicio" },
  { to: "/movimientos", label: "Movimientos", icon: ArrowLeftRight, tid: "nav-movimientos" },
  { to: "/por-pagar", label: "Por pagar", icon: CalendarClock, tid: "nav-por-pagar" },
  { to: "/mi-dinero", label: "Mi dinero", icon: Wallet, tid: "nav-mi-dinero" },
];

export default function Sidebar() {
  const loc = useLocation();
  const nav = useNavigate();
  const { user, logout } = useAuth();
  const [more, setMore] = useState(false);

  const handleLogout = async () => { await logout(); nav("/login"); };

  return (
    <>
      <aside className="hidden lg:flex fixed top-0 left-0 w-64 h-screen flex-col bg-[#0e0d14] border-r border-white/5 z-40"
        data-testid="sidebar">
        <div className="px-6 pt-7 pb-6">
          <Link to="/" className="flex items-center gap-2.5">
            <img src="/logo.png" alt="" className="w-9 h-9 rounded-xl ring-1 ring-white/10" />
            <span className="text-[14px] font-bold tracking-tight text-white">THE TRADING DOCK</span>
          </Link>
        </div>

        <button onClick={() => window.dispatchEvent(new CustomEvent("td:quick"))}
          data-testid="sidebar-quick"
          className="mx-3 mb-4 flex items-center justify-center gap-2 min-h-12 rounded-2xl bg-white text-[#15141b] font-bold">
          <Plus className="w-5 h-5" strokeWidth={2.5} aria-hidden="true" /> Anotar algo
        </button>

        <nav className="flex-1 px-3 space-y-1" aria-label="Principal">
          {MAIN_NAV.map(({ to, label, icon: Icon, tid }) => {
            const active = to === "/" ? loc.pathname === "/" : loc.pathname.startsWith(to);
            return (
              <Link key={to} to={to} data-testid={tid} aria-current={active ? "page" : undefined}
                className={`flex items-center gap-3 px-4 min-h-12 rounded-xl transition-colors ${
                  active ? "bg-white/10 text-white" : "text-slate-300 hover:text-white hover:bg-white/5"}`}>
                <Icon className="w-[18px] h-[18px]" strokeWidth={2} aria-hidden="true" />
                <span className="text-[15px] font-medium">{label}</span>
              </Link>
            );
          })}
          <button onClick={() => setMore(true)} data-testid="nav-mas"
            className="w-full flex items-center gap-3 px-4 min-h-12 rounded-xl text-slate-300 hover:text-white hover:bg-white/5">
            <MoreHorizontal className="w-[18px] h-[18px]" strokeWidth={2} aria-hidden="true" />
            <span className="text-[15px] font-medium">Más</span>
          </button>
        </nav>

        <div className="p-4 border-t border-white/5">
          <div className="flex items-center gap-3 px-2 py-2.5 rounded-xl bg-white/[0.03]">
            <Link to="/perfil" className="flex items-center gap-3 flex-1 min-w-0" data-testid="sidebar-profile">
              <span className="w-9 h-9 rounded-full bg-violet-600 flex items-center justify-center text-white text-sm font-semibold shrink-0">
                {user?.name?.[0]?.toUpperCase() || "U"}
              </span>
              <span className="min-w-0">
                <span className="block text-[14px] text-white font-medium truncate">{user?.name}</span>
                <span className="block text-[12px] text-slate-400 truncate">{user?.email}</span>
              </span>
            </Link>
            <button onClick={handleLogout} data-testid="sidebar-logout" aria-label="Cerrar sesión"
              className="p-2 rounded-lg text-slate-300 hover:text-rose-300 hover:bg-rose-500/10 min-h-11 min-w-11 flex items-center justify-center">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      <MoreSheet open={more} onOpenChange={setMore} />
    </>
  );
}
